import React, { useState} from 'react';
import { MapPin, Bell, Store, ChevronRight, Check, X } from 'lucide-react';

/* ─────────────────────────────────────────────────────────────────
   Clé localStorage — ne s'affiche qu'une seule fois
───────────────────────────────────────────────────────────────── */
const STORAGE_KEY = 'yeldi_onboarding_done';

export const hasSeenOnboarding = () =>
  !!localStorage.getItem(STORAGE_KEY);

export const markOnboardingDone = () =>
  localStorage.setItem(STORAGE_KEY, '1');

/* ─────────────────────────────────────────────────────────────────
   Slides de l'onboarding
───────────────────────────────────────────────────────────────── */
const SLIDES = [
  {
    id: 'welcome',
    gradient: 'linear-gradient(145deg, #0b142d 0%, #1b2a6b 60%, #00c9a7 100%)',
    illustration: (
      <div style={{ position: 'relative', width: 90, height: 90, margin: '0 auto' }}>
        {/* Cercles décoratifs */}
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          background: 'rgba(0,201,167,0.15)',
          animation: 'obPulse 2.5s ease-in-out infinite',
        }}/>
        <div style={{
          position: 'absolute', inset: 10, borderRadius: '50%',
          background: 'rgba(0,201,167,0.20)',
        }}/>
        <div style={{
          position: 'absolute', inset: 20, borderRadius: '50%',
          background: 'linear-gradient(135deg, #1b2a6b, #00c9a7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 24px rgba(0,201,167,0.40)',
        }}>
          <Store size={24} color="#fff" strokeWidth={1.8}/>
        </div>
      </div>
    ),
    title: 'Bienvenue sur Yeldi !',
    body: 'Découvrez en temps réel quels commerces autour de vous sont ouverts ou fermés — avant de vous déplacer.',
    cta: 'Commencer',
    skip: true,
  },
  {
    id: 'geolocation',
    gradient: 'linear-gradient(145deg, #0b142d 0%, #1e3a8a 60%, #3b82f6 100%)',
    illustration: (
      <div style={{ position: 'relative', width: 90, height: 90, margin: '0 auto' }}>
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          background: 'rgba(59,130,246,0.15)',
          animation: 'obPulse 2.5s ease-in-out infinite',
        }}/>
        <div style={{
          position: 'absolute', inset: 20, borderRadius: '50%',
          background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 24px rgba(59,130,246,0.40)',
        }}>
          <MapPin size={24} color="#fff" strokeWidth={1.8}/>
        </div>
      </div>
    ),
    title: 'Votre position',
    body: 'Pour voir les commerces près de chez vous, Yeldi a besoin de votre position. Votre localisation reste sur votre appareil et n\'est jamais partagée.',
    cta: 'Autoriser la position',
    ctaSecondary: 'Pas maintenant',
    permType: 'geo',
    skip: false,
  },
  {
    id: 'notifications',
    gradient: 'linear-gradient(145deg, #0b142d 0%, #312e81 60%, #6366f1 100%)',
    illustration: (
      <div style={{ position: 'relative', width: 90, height: 90, margin: '0 auto' }}>
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          background: 'rgba(99,102,241,0.15)',
          animation: 'obPulse 2.5s ease-in-out infinite',
        }}/>
        <div style={{
          position: 'absolute', inset: 20, borderRadius: '50%',
          background: 'linear-gradient(135deg, #312e81, #6366f1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 24px rgba(99,102,241,0.40)',
        }}>
          <Bell size={24} color="#fff" strokeWidth={1.8}/>
        </div>
        {/* Badge notification décoratif */}
        <div style={{
          position: 'absolute', top: 14, right: 14,
          width: 18, height: 18, borderRadius: '50%',
          background: '#ef4444',
          border: '2px solid rgba(255,255,255,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.6rem', fontWeight: 800, color: '#fff',
        }}>1</div>
      </div>
    ),
    title: 'Restez informé',
    body: 'Recevez une alerte quand un de vos commerces favoris ouvre ou ferme. Vous pouvez désactiver ça à tout moment.',
    cta: 'Activer les alertes',
    ctaSecondary: 'Non merci',
    permType: 'notif',
    skip: false,
  },
  {
    id: 'done',
    gradient: 'linear-gradient(145deg, #0b142d 0%, #064e3b 60%, #00c9a7 100%)',
    illustration: (
      <div style={{ position: 'relative', width: 90, height: 90, margin: '0 auto' }}>
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          background: 'rgba(0,201,167,0.15)',
          animation: 'obPulse 2s ease-in-out infinite',
        }}/>
        <div style={{
          position: 'absolute', inset: 20, borderRadius: '50%',
          background: 'linear-gradient(135deg, #065f46, #00c9a7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 24px rgba(0,201,167,0.50)',
        }}>
          <Check size={26} color="#fff" strokeWidth={2.5}/>
        </div>
      </div>
    ),
    title: 'Vous êtes prêt !',
    body: 'Explorez les commerces autour de vous. Appuyez sur un magasin pour voir son statut en détail.',
    cta: 'Découvrir Yeldi',
    skip: false,
  },
];

/* ─────────────────────────────────────────────────────────────────
   COMPOSANT PRINCIPAL
───────────────────────────────────────────────────────────────── */
const OnboardingModal = ({ onDone }) => {
  const [step,    setStep   ] = useState(0);
  const [exiting, setExiting] = useState(false);
  const [permLoading, setPermLoading] = useState(false);

  const slide = SLIDES[step];
  const isLast = step === SLIDES.length - 1;

  /* ── Fermeture avec animation ── */
  const close = () => {
    setExiting(true);
    markOnboardingDone();
    setTimeout(() => onDone(), 280);
  };

  /* ── Slide suivant ── */
  const next = () => {
    if (isLast) { close(); return; }
    setStep(s => s + 1);
  };

  /* ── Demande de permission géoloc ── */
  const requestGeo = () => {
    if (!('geolocation' in navigator)) { next(); return; }
    setPermLoading(true);
    navigator.geolocation.getCurrentPosition(
      () => { setPermLoading(false); next(); },
      () => { setPermLoading(false); next(); },
      { timeout: 12000, maximumAge: 0, enableHighAccuracy: true }
    );
  };

  /* ── Demande de permission notifications ── */
  const requestNotif = async () => {
    if (!('Notification' in window)) { next(); return; }
    setPermLoading(true);
    try { await Notification.requestPermission(); } catch (_) {}
    setPermLoading(false);
    next();
  };

  /* ── Action du bouton principal selon le type de slide ── */
  const handleCta = () => {
    if (slide.permType === 'geo')   { requestGeo();   return; }
    if (slide.permType === 'notif') { requestNotif(); return; }
    next();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      animation: exiting ? 'obFadeOut 0.28s ease forwards' : 'obFadeIn 0.22s ease',
    }}>
      <div style={{
        width: '100%', maxWidth: 480,
        background: 'var(--bg)',
        borderRadius: 'var(--r-xl) var(--r-xl) 0 0',
        overflow: 'hidden',
        boxShadow: '0 -8px 40px rgba(0,0,0,0.3)',
        animation: exiting ? 'obSlideDown 0.28s ease forwards' : 'obSlideUp 0.28s cubic-bezier(0.32,0.72,0,1)',
      }}>

        {/* ── Hero coloré ── */}
        <div style={{
          background: slide.gradient,
          padding: '2rem 1.5rem 1.75rem',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          position: 'relative',
          transition: 'background 0.4s ease',
        }}>
          {/* Bouton passer */}
          {slide.skip && (
            <button
              onClick={close}
              style={{
                position: 'absolute', top: '0.85rem', right: '0.85rem',
                background: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.25)',
                borderRadius: '999px', padding: '0.25rem 0.65rem',
                color: 'rgba(255,255,255,0.80)', fontSize: '0.72rem',
                fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: '0.25rem',
              }}
            >
              Passer <X size={11}/>
            </button>
          )}

          {/* Illustration */}
          <div style={{ marginBottom: '1.25rem' }}>
            {slide.illustration}
          </div>

          {/* Dots de progression */}
          <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem' }}>
            {SLIDES.map((_, i) => (
              <div key={i} style={{
                height: 4,
                width: i === step ? 20 : 8,
                borderRadius: 2,
                background: i === step
                  ? 'rgba(255,255,255,0.95)'
                  : i < step
                    ? 'rgba(255,255,255,0.55)'
                    : 'rgba(255,255,255,0.25)',
                transition: 'all 0.3s ease',
              }}/>
            ))}
          </div>
        </div>

        {/* ── Contenu texte ── */}
        <div style={{ padding: '1.4rem 1.4rem 2rem' }}>
          <h2 style={{
            fontSize: '1.1rem', fontWeight: 800,
            color: 'var(--text)', marginBottom: '0.55rem',
            letterSpacing: '-0.25px', lineHeight: 1.25,
          }}>
            {slide.title}
          </h2>
          <p style={{
            fontSize: '0.83rem', color: 'var(--text-2)',
            lineHeight: 1.6, marginBottom: '1.4rem',
          }}>
            {slide.body}
          </p>

          {/* ── Bouton principal ── */}
          <button
            onClick={handleCta}
            disabled={permLoading}
            style={{
              width: '100%', padding: '0.88rem',
              borderRadius: 'var(--r-lg)', border: 'none',
              background: permLoading
                ? 'var(--outline)'
                : 'linear-gradient(135deg, #1b2a6b, #00c9a7)',
              color: '#fff', fontWeight: 700, fontSize: '0.95rem',
              cursor: permLoading ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '0.45rem',
              boxShadow: permLoading ? 'none' : '0 4px 18px rgba(0,201,167,0.35)',
              transition: 'all 0.2s',
              marginBottom: slide.ctaSecondary ? '0.55rem' : 0,
            }}
          >
            {permLoading ? (
              <>
                <div style={{
                  width: 16, height: 16,
                  border: '2px solid rgba(255,255,255,0.4)',
                  borderTopColor: '#fff', borderRadius: '50%',
                  animation: 'obSpin 0.7s linear infinite',
                }}/>
                En attente…
              </>
            ) : (
              <>
                {slide.cta}
                {isLast ? <Check size={17}/> : <ChevronRight size={17}/>}
              </>
            )}
          </button>

          {/* ── Bouton secondaire (skip permission) ── */}
          {slide.ctaSecondary && !permLoading && (
            <button
              onClick={next}
              style={{
                width: '100%', padding: '0.72rem',
                borderRadius: 'var(--r-lg)', border: 'none',
                background: 'transparent',
                color: 'var(--text-3)', fontWeight: 600, fontSize: '0.85rem',
                cursor: 'pointer', fontFamily: 'inherit',
                transition: 'color 0.15s',
              }}
            >
              {slide.ctaSecondary}
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes obFadeIn   { from { opacity:0 }               to { opacity:1 } }
        @keyframes obFadeOut  { from { opacity:1 }               to { opacity:0 } }
        @keyframes obSlideUp  { from { transform:translateY(100%) } to { transform:translateY(0) } }
        @keyframes obSlideDown{ from { transform:translateY(0) } to { transform:translateY(100%) } }
        @keyframes obPulse    { 0%,100% { transform:scale(1); opacity:1 } 50% { transform:scale(1.08); opacity:0.7 } }
        @keyframes obSpin     { to { transform:rotate(360deg) } }
      `}</style>
    </div>
  );
};

export default OnboardingModal;